<template>
  <v-card
    class="simulation-result-container elevation-7 mt-1"
    max-width="15vw"
    min-width="15vw"
  >
    <AppOverlay v-model="loading">
      <AppLoadingSpinner text="シミュレーション実行中..." class="ma-3" />
    </AppOverlay>
    <v-card-title
      class="font-weight-bold"
      style="padding-top: 8px; padding-bottom: 0;"
    >
      概算シミュレーション
    </v-card-title>
    <v-card-text style="padding: 10px;">
      <template v-if="visible">
        <v-layout colmun class="mb-2">
          <v-flex>
            ユーザー数
          </v-flex>
          <v-flex class="text-xs-right">
            <AppNumberWithUnit
              :value="simulation.uu"
              unit="UU"
              local-string
              fallback-text="−"
            />
          </v-flex>
        </v-layout>
        <v-layout colmun class="mb-2">
          <v-flex>
            配信日数
          </v-flex>
          <v-flex class="text-xs-right">
            <AppNumberWithUnit
              :value="deliveryDays"
              unit="日"
              fallback-text="−"
            />
          </v-flex>
        </v-layout>
      </template>
      <v-layout colmun class="mb-3">
        <v-flex>
          消化見込み額
        </v-flex>
        <v-flex class="text-xs-right">
          <AppNumberWithUnit
            v-if="!isNotPrescribedSize"
            :value="simulation.revenue"
            prefix="¥"
            local-string
            fallback-text="−"
          />
          <AppNumberWithUnit v-else unit="−" prefix="¥" local-string />
        </v-flex>
      </v-layout>
      <v-container v-if="errorMessages" style="padding: 0">
        <v-layout>
          <v-flex xs2>
            <v-btn @click="isOpen = !isOpen" absolute icon small>
              <v-icon v-if="isOpen">keyboard_arrow_up</v-icon>
              <v-icon v-else>keyboard_arrow_down</v-icon>
            </v-btn>
          </v-flex>
          <v-flex xs2>
            <v-icon
              style="position: relative; top: 2px; left: 8px; font-size: 20px; color: #fbc02d;"
            >
              warning
            </v-icon>
          </v-flex>
          <v-flex xs2>
            <span style="position: relative; top: 2px">注意</span>
          </v-flex>
        </v-layout>
        <v-layout v-if="isOpen" column>
          <v-flex
            v-for="(message, index) in errorMessages"
            :key="index"
            class="red--text caption"
            style="padding-top: 6px; padding-bottom: 6px"
          >
            {{ message }}
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { Subject } from 'rxjs'
import { mapActions, mapGetters } from 'vuex'
import {
  switchMap,
  debounceTime,
  tap,
  takeUntil,
  map,
  filter,
} from 'rxjs/operators'
import {
  SimulationRepository,
  SimulationSubjectBuilder,
} from '@/models/Simulation'
import { translateCampaignToSimulation } from '@/models/CampaignOperation/Form'
import { snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import getLocationsCacheable from '@/models/CampaignOperation/Form/getLocationsCacheable'
import { runInBatches } from '@/models/runInBatches'
import { BannerRepository } from '@/models/Creative/BannerRepository'
import { CreativeTypes } from '@/models/Creative'

const DEBOUNCE_TIME = 1200
const POLLING_TIMEOUT = 60 * 1000 // 1分
const POLLING_INTERVAL = 1000 // 1秒間隔

const CREATIVE_SETTING_PAGE = 7

const maxLocationGroup = 250

export default {
  name: 'CampaignSimulationResult',
  props: {
    form: {
      type: Object,
      required: true,
      default: () => {},
    },
    advertiser: {
      type: Object,
      required: true,
      default: () => {},
    },
    minUU: {
      type: Number,
      required: true,
      default: null,
    },
    step: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      onDestroy$: new Subject(),
      simulationResult: null,
      loading: false,
      getLocationsCacheable: getLocationsCacheable(),
      isOpen: false,
      isNotPrescribedSize: false,
    }
  },
  computed: {
    ...mapGetters('campaign', ['getLocationListCount']),
    ...mapGetters('campaign', ['getLocationsById']),
    ...mapGetters('account', ['isValidateSimulation']),
    menuSetting() {
      return this.form.campaign.menuSetting
    },
    locationGroupError() {
      if (this.locationListCount >= maxLocationGroup) return true
      return false
    },
    showErrorMessage() {
      return this.isInvalidUU && this.isValidateSimulation
    },
    locationListCount() {
      return this.getLocationListCount
    },
    simulation() {
      if (
        !this.simulationResult ||
        this.locationListCount >= maxLocationGroup ||
        !this.form.isSimulatable
      ) {
        return false
      }
      return this.simulationResult.simulation
    },
    deliveryDays() {
      if (
        !this.simulationResult ||
        this.locationListCount >= maxLocationGroup ||
        !this.form.isSimulatable
      ) {
        return false
      }
      return this.simulationResult.deliveryDays
    },
    isInvalidUU() {
      if (!this.simulationResult) {
        return false
      }
      return this.minUU > this.simulation.uu
    },
    isNotPublic() {
      if (!this.form.campaign.hasGeoMenuSetting) {
        return false
      }
      return this.menuSetting.deliveryLocationGroups
        .map(dlg => !(dlg.accessibleType === 'public'))
        .includes(true)
    },
    isAboveMaxRadius() {
      return this.form.isAboveMaxRadius
    },
    visible() {
      return this.step !== CREATIVE_SETTING_PAGE
    },
    isCreativeScheduleSettingPage() {
      return (
        this.step === CREATIVE_SETTING_PAGE &&
        this.form.campaign.useCreativeSchedule
      )
    },
    showWarningIcon() {
      return (
        this.isCreativeScheduleSettingPage &&
        (this.isInvalidGeneralSimulation || this.isNotPublic)
      )
    },
    isBanner() {
      return this.form.campaign.creativeType === CreativeTypes.BANNER
    },
    errorMessages() {
      if (!this.isBanner) return false
      const messages = []
      if (this.locationGroupError) {
        messages.push(
          '地点の合計が250以上設定されているため、シミュレーションは実行できません',
        )
      }
      if (this.showErrorMessage) {
        messages.push(
          '配信対象ユーザー数が' +
            this.minUU +
            'UU以下の場合 入稿を完了出来ません。半径や高度な設定の条件を緩和して下さい。',
        )
      }
      if (this.isNotPublic) {
        messages.push(
          '非公開の地点グループは、シミュレーションの結果には反映されていません。',
        )
      }
      if (this.isAboveMaxRadius) {
        messages.push(
          'シミュレーションが可能な最大半径は、10,000mです。半径設定が10,000mを超えているため、シミュレーションは実行されません。',
        )
      }
      if (this.isNotPrescribedSize) {
        messages.push(
          '消化見込み額は、300x250, 320x50の2サイズ以外を設定した場合、算出されません。',
        )
      }
      return messages.length > 0 ? messages : false
    },
  },
  watch: {
    'form.campaign.creatives': {
      immediate: true,
      deep: true,
      handler: function() {
        this.isNotPrescribedCreativeSize()
      },
    },
  },
  created() {
    // 各Subjectを初期化
    this.initializeSubject()

    // 各Subjectを購読
    this.subscriptionForm()
    this.subscriptionSimulation()

    this.$watch(
      () => [
        this.form.isSimulatable,
        this.menuSetting,
        this.form.campaign.advancedSetting.demographicSetting,
        this.form.campaign.menuId,
        this.form.campaign.creativeAttachTarget,
        this.form.campaign.creatives,
        this.form.campaign.campaignBudgetsCreatives,
        this.form.campaign.creativeType,
        this.form.campaign.useAdvancedSetting,
        this.form.campaign.advancedSetting.deliveryDevices,
        this.form.campaign.advancedSetting.deliveryInventories,
        this.form.campaign.advancedSetting.onlyJapaneseDevice,
        this.form.campaign.advancedSetting.weeklyDeliverySchedule,
        this.form.campaign.mergedCampaignBudgets.map(cb => cb.startTime),
        this.form.campaign.mergedCampaignBudgets.map(cb => cb.endTime),
      ],
      () => this.formSubject$.next(this.form),
      {
        deep: true,
      },
    )
  },
  destroyed() {
    this.onDestroy$.next()
  },
  methods: {
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    initializeSubject() {
      this.formSubject$ = new Subject()
      this.simulationSubject$ = SimulationSubjectBuilder.createBuilder()
        .addInterval(POLLING_INTERVAL)
        .addTimeout(POLLING_TIMEOUT)
        .build()
    },
    isActiveGroup(group) {
      return group.deleted === false && group.accessibleType === 'public'
    },
    subscriptionForm() {
      this.formSubject$
        .pipe(
          debounceTime(DEBOUNCE_TIME),
          filter(form => form.isSimulatable),
          filter(() => !this.locationGroupError),
          tap(() => this.startLoading()),
          map(form => this.assignCampaignBudgets(form)),
          switchMap(form => this.assignLocationsToGroup(form)),
          switchMap(form =>
            translateCampaignToSimulation(form, this.advertiser),
          ),
          switchMap(simulationForm =>
            SimulationRepository.create(simulationForm),
          ),
          takeUntil(this.onDestroy$),
        )
        .subscribe(
          ({ conditionHash }) => this.simulationSubject$.next(conditionHash),
          error => this.onSimulationError(error),
        )
    },
    subscriptionSimulation() {
      this.simulationSubject$
        .pipe(
          tap(() => this.endLoading()),
          takeUntil(this.onDestroy$),
        )
        .subscribe(
          response => this.onSimulationSuccess(response),
          error => this.onSimulationError(error),
        )
    },
    onSimulationSuccess(response) {
      this.endLoading()
      this.simulationResult = response.item.result
      this.$emit('simulated', this.simulationResult)
    },
    onSimulationError(error) {
      // シミュレーションへのデータ変換エラーを出力
      console.log(error)
      this.endLoading()
      this.showErrorSnackbar()

      /**
       *  一度例外が出たSubjectは破壊されるため
       *  Subjectを再構築する
       */
      this.initializeSubject()
      this.subscriptionForm()
      this.subscriptionSimulation()
    },
    startLoading() {
      this.loading = true
    },
    endLoading() {
      this.loading = false
    },
    showErrorSnackbar() {
      this.showSnackbar({
        type: snackbarType.ERROR,
        message: 'シミュレーションの取得に失敗しました',
      })
    },
    async assignLocationsToGroup(form) {
      /**
       * 地点情報の取得方法が異なるためForm.deliveryLocationGroupsを
       * 地点グループとシミュレーション地点グループに分類する
       */
      const { locationGroups, simulationLocationGroups } = this.classifyGroups(
        form.campaign.menuSetting.visibleDeliveryLocationGroups,
      )

      /** 地点グループに属する地点情報を割り当てる */
      const groups = await this.assignLocations(locationGroups)

      /** シミュレーショングループに属する地点情報を割り当てる */
      const groupsFromSimulation = this.assignSimulationLocations(
        simulationLocationGroups,
      )

      /** 所属が違う2つの地点グループをマージする */
      const deliveryLocationGroups = [...groupsFromSimulation, ...groups]
      return {
        ...form,
        deliveryLocationGroups,
      }
    },
    classifyGroups(groups) {
      return groups.reduce(
        (acc, group) => {
          if (group.isSimulation) {
            acc.simulationLocationGroups.push(group)
            return acc
          }
          acc.locationGroups.push(group)
          return acc
        },
        { locationGroups: [], simulationLocationGroups: [] },
      )
    },
    assignCampaignBudgets(form) {
      const campaignBudgets = form.getNoRejectedCampaignBudgets()
      return Object.assign(form, { campaignBudgets })
    },
    assignSimulationLocations(groups) {
      return groups.map(group => {
        return {
          ...group,
          simulationLocations: this.getLocationsById(group.id),
        }
      })
    },
    async assignLocations(groups) {
      const BATCH_SIZE = 10
      const locationsByGroup = await runInBatches(
        groups.map(group => () =>
          this.getLocationsCacheable(group.locationType, group.locationGroupId),
        ),
        BATCH_SIZE,
      )

      const locationsMap = locationsByGroup.reduce((acc, locations) => {
        acc.set(locations[0].locationGroupId, locations)
        return acc
      }, new Map())
      return groups.map(group => {
        return {
          ...group,
          simulationLocations: locationsMap.get(group.locationGroupId),
        }
      })
    },
    getCreativesInfo() {
      return this.form.campaign.creatives.map(creative => {
        return BannerRepository.get(creative.id)
      })
    },
    async isNotPrescribedCreativeSize() {
      if (this.isBanner) {
        await Promise.all(this.getCreativesInfo()).then(creatives => {
          this.isNotPrescribedSize = creatives.some(
            creative =>
              !(
                (creative.width === 300 && creative.height === 250) ||
                (creative.width === 320 && creative.height === 50)
              ),
          )
        })
      }
    },
  },
}
</script>

<style scoped>
.simulation-result-container {
  z-index: 2;
  position: absolute;
  right: 0;
}
</style>
